import { TreeList, Spinner } from '@ibsys/kit';
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { BrainCircuitIcon, GripVerticalIcon, PackageIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateKmAssessment, useCreateKmGroup, useKmStructure } from '@/entities/expert-report-km';
import { cn } from '@/shared/lib';
import { DropIndicator } from '../ExpertReportTree/DropIndicator';
import { KmReportNodeActions } from './KmReportNodeActions';
import { KmStructureTreeNode } from './KmStructureTreeNode';
import { useKmStructureDnd } from './useKmStructureDnd';
import { useKmTreeIndexes } from './useKmTreeIndexes';
import {
  kmAssessmentNodeId,
  kmGroupNodeId,
  kmReportNodeId,
  structureToTreeNodes,
  type KmTreeNodeData,
} from './kmTreeUtils';

type Props = {
  reportId: number;
  reportName: string;
  isExpanded: boolean;
  /** Все раскрытые узлы дерева — по ним определяются свёрнутые группы для авто-разворачивания */
  expandedIds: string[];
  activeId: string;
  onSelectReport: () => void;
  onSelectNode: (node: KmTreeNodeData) => void;
  onExpandNodes: (ids: string[]) => void;
  isDesktop?: boolean;
};

const ICON_SIZE = 18;

/**
 * Заключение на модели знаний в дереве заключений: строка самого заключения и его структура —
 * дерево групп с единицами оценивания. Отдельный узел, а не ветка внутри узла заключения на
 * блоках и правилах: структура читается из другого раздела и правится другими операциями.
 */
export const KmReportTreeNode: FC<Props> = ({
  reportId,
  reportName,
  isExpanded,
  expandedIds,
  activeId,
  onSelectReport,
  onSelectNode,
  onExpandNodes,
  isDesktop,
}) => {
  const { t } = useTranslation('expertReport');
  const { data: structure, isLoading } = useKmStructure({ reportId }, { enabled: isExpanded });
  const { mutate: createGroup, isPending: isCreatingGroup } = useCreateKmGroup();
  const { mutate: createAssessment, isPending: isCreatingAssessment } = useCreateKmAssessment();

  const treeRef = useRef<HTMLDivElement>(null);
  const nodeId = kmReportNodeId(reportId);
  const isActive = activeId === nodeId;

  const nodes = useMemo(() => (structure ? structureToTreeNodes(structure, reportId) : []), [structure, reportId]);
  const { leafGroupIds, collapsedGroupIds, descendantsByNodeId, labelByNodeId } = useKmTreeIndexes(nodes, expandedIds);
  const { handleDragStart, handleDragEnd } = useKmStructureDnd({
    reportId,
    rootGroupId: structure?.rootId ?? null,
    nodes,
    leafGroupIds,
  });

  const handleAddGroup = useCallback(
    (parentGroupId: number | null) => {
      createGroup(
        { reportId, parentId: parentGroupId, name: t('km.newGroupName') },
        {
          onSuccess: (group) => {
            onExpandNodes([String(reportId), ...(parentGroupId ? [kmGroupNodeId(reportId, parentGroupId)] : [])]);
            onSelectNode({
              id: kmGroupNodeId(reportId, group.id),
              label: group.name,
              type: 'group',
              reportId,
              entityId: group.id,
            });
          },
        },
      );
    },
    [createGroup, onExpandNodes, onSelectNode, reportId, t],
  );

  const handleAddAssessment = useCallback(
    (groupId: number | null) => {
      // Единица оценивания всегда лежит в группе: верхний уровень заключения — его корневая группа
      const targetGroupId = groupId ?? structure?.rootId;
      if (!targetGroupId) return;

      createAssessment(
        { groupId: targetGroupId, name: t('km.newAssessmentName') },
        {
          onSuccess: (assessment) => {
            onExpandNodes([String(reportId), ...(groupId ? [kmGroupNodeId(reportId, groupId)] : [])]);
            onSelectNode({
              id: kmAssessmentNodeId(reportId, assessment.id),
              label: assessment.name,
              type: 'assessment',
              reportId,
              entityId: assessment.id,
            });
          },
        },
      );
    },
    [createAssessment, onExpandNodes, onSelectNode, reportId, structure?.rootId, t],
  );

  const handleExpandAll = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      const ids: string[] = [String(reportId)];
      const collect = (items: KmTreeNodeData[]) => {
        for (const item of items) {
          if (item.children?.length) {
            ids.push(item.id);
            collect(item.children);
          }
        }
      };
      collect(nodes);
      onExpandNodes(ids);
    },
    [nodes, onExpandNodes, reportId],
  );

  const handleExpandNode = useCallback((id: string) => onExpandNodes([id]), [onExpandNodes]);

  return (
    <TreeList.Group
      value={String(reportId)}
      classNames={{ header: 'group/km-report' }}
      label={
        <div className="flex flex-1 items-start gap-2">
          <div
            className={cn(
              'mt-0.5 shrink-0',
              isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover:text-foreground',
            )}
          >
            <BrainCircuitIcon size={ICON_SIZE} />
          </div>
          <span className="line-clamp-2 flex-1 text-left text-sm font-medium leading-tight">{reportName}</span>
        </div>
      }
      onClick={() => {
        // На десктопе клик по строке открывает карточку, на узком экране — только раскрывает узел
        if (isDesktop) onSelectReport();
      }}
      rightSlot={
        <KmReportNodeActions
          isCreating={isCreatingGroup || isCreatingAssessment}
          onOpenCard={onSelectReport}
          onExpandAll={handleExpandAll}
          onAddGroup={() => handleAddGroup(null)}
          onAddAssessment={() => handleAddAssessment(null)}
          isDesktop={isDesktop}
        />
      }
    >
      <div className="relative" ref={treeRef}>
        {isExpanded && isLoading && (
          <div className="flex items-center gap-2">
            <Spinner className="size-4" />
            <span className="text-xs text-muted-foreground">{t('loading')}</span>
          </div>
        )}

        {isExpanded && !isLoading && nodes.length > 0 && (
          <DragDropProvider
            sensors={[
              PointerSensor.configure({
                activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })],
              }),
            ]}
            modifiers={[RestrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {nodes.map((node, index) => (
              <KmStructureTreeNode
                key={node.id}
                node={node}
                index={index}
                containerId={nodeId}
                activeId={activeId}
                onSelect={onSelectNode}
                onAddGroup={handleAddGroup}
                onAddAssessment={handleAddAssessment}
                onExpandNode={handleExpandNode}
                isDesktop={isDesktop}
              />
            ))}
            {/* Плавающая копия перетаскиваемого узла — полупрозрачная, чтобы сквозь неё был
                виден индикатор места вставки */}
            <DragOverlay dropAnimation={null}>
              {(source) => {
                const label = labelByNodeId.get(String(source.id));
                if (!label) return null;
                return (
                  <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-card/60 px-3 py-2 shadow-sm ring-1 ring-border">
                    <PackageIcon size={14} className="shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium">{label}</span>
                    <GripVerticalIcon size={14} className="ml-auto shrink-0 text-muted-foreground" />
                  </div>
                );
              }}
            </DragOverlay>
            <DropIndicator
              containerRef={treeRef}
              leafNodeIds={leafGroupIds}
              collapsedGroupIds={collapsedGroupIds}
              descendantsByNodeId={descendantsByNodeId}
              onAutoExpand={handleExpandNode}
            />
          </DragDropProvider>
        )}

        {isExpanded && !isLoading && nodes.length === 0 && (
          <div className="select-none text-xs italic text-muted-foreground">{t('emptyNode')}</div>
        )}
      </div>
    </TreeList.Group>
  );
};
